import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spinner, SpinnerSize } from '@fluentui/react'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import { Destination, IGraphArrayResponse } from '../../context/AxiosContext'
import { driveItemOverviewProperties, formatFolderName } from '../../helpers/documentHelper'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { Axios, AxiosResponse } from 'axios'
import { parseRetryAfter, PATIENT_RETRY_HEADER } from '../../helpers/graphRetry'
import {
    FlatTree,
    FlatTreeItemProps,
    FlatTreeProps,
    HeadlessFlatTreeItem,
    HeadlessFlatTreeItemProps,
    makeStaticStyles,
    makeStyles,
    mergeClasses,
    MessageBar,
    MessageBarBody,
    tokens,
    useHeadlessFlatTree_unstable,
} from '@fluentui/react-components'
import { HeadlessFlatTree } from '@fluentui/react-tree'
import SimpleDocumentFlatTreeItem from './SimpleDocumentFlatTreeItem'
import { DocumentSortOrder } from '../../config/documentConfig'
import { DateTime } from 'luxon'
import FileDocumentSearchBox from '../../v2/pages/File/ViewFileTabs/FileDocumentSearchBox'
import { appInsights, useApplicationStyles } from '../../pages/App'
import { selectAllVisibleFiles, selectRange, toggleSelect } from './tree/documentTreeSelection'
import { canDropOnRoot, getDragIds, HAMMOCK_DRAG_MIME, isRecursiveDrop } from './tree/documentTreeDrag'
import { TreeRow } from './tree/documentTreeTypes'
import { stealthClick } from '../StealthLink'
import { DocumentOpenType } from '../../context/GlobalContext'

const useStyles = makeStyles({
    // content-visibility lets the browser skip layout/paint for tree items that are off-screen.
    // contain-intrinsic-size reserves the right amount of space so the scrollbar stays accurate.
    // Applied to every treeitem descendant of FlatTree so large folders (>1000 items) stay responsive.
    virtualizedTree: {
        '& [role="treeitem"]': {
            contentVisibility: 'auto',
            containIntrinsicSize: '0 32px',
        },
    },
    // Explorer mode (HAM-3993): compact per-level indent (Fluent's default is spacingHorizontalXXL
    // per level, far too wide for the 300px Outlook pane) plus a thin vertical line marking the
    // hierarchy of everything below level 1. The line sits at the parent's indent position.
    explorerTree: {
        '& [role="treeitem"]': {
            '--spacingHorizontalXXL': tokens.spacingHorizontalS,
            position: 'relative',
            cursor: 'pointer',
        },
        '& [role="treeitem"]:not([aria-level="1"])::before': {
            content: '""',
            position: 'absolute',
            left: `calc((var(--fluent-TreeItem--level, 1) - 1) * ${tokens.spacingHorizontalS})`,
            top: 0,
            bottom: 0,
            width: '1px',
            backgroundColor: tokens.colorNeutralStroke2,
        },
    },
    dragAndDropSpacing: {
        marginTop: tokens.spacingVerticalM,
    },
})

// Drag & drop feedback classes are toggled with classList.add/remove on existing DOM nodes
// (never via a re-render: a re-render during dragover would replace the row elements and kill
// the drag). makeStaticStyles gives them stable global names; colors stay theme-aware because
// Fluent tokens are plain CSS variables.
const useDragStaticStyles = makeStaticStyles({
    '.hammockDocTreeDragging': {
        opacity: '0.5',
    },
    '.hammockDocTreeDropInside': {
        backgroundColor: 'var(--colorBrandBackground2)',
        boxShadow: 'inset 0 0 0 1px var(--colorBrandStroke1)',
    },
    // Container-wide marker while hovering the empty space below the rows: the drop target is
    // the dossier root, so the whole tree gets an inset outline instead of a row highlight.
    '.hammockDocTreeDropRoot': {
        boxShadow: 'inset 0 0 0 2px var(--colorBrandStroke1)',
    },
    // While an internal drag is running the container normally hugs its rows, leaving no
    // empty space to drop on — this padding opens up a root drop zone below the last row.
    '.hammockDocTreeDragActive': {
        paddingBottom: '40px',
    },
    '.hammockDocTreeDragGhost': {
        position: 'fixed',
        top: '-1000px',
        left: '-1000px',
        padding: '4px 10px',
        borderRadius: '4px',
        backgroundColor: 'var(--colorBrandBackground)',
        color: 'var(--colorNeutralForegroundOnBrand)',
        fontSize: '12px',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
    },
})

// Translation keys for the three load-failure states (HAM-673): no SharePoint link configured,
// folder no longer exists (404) and no access to the folder (403). Overridable per context so
// the CRM tab can phrase them for a relation instead of a dossier.
export type DocumentFlatTreeMessageKeys = {
    noLink: string
    noLinkEdit: string
    folderNotFound: string
    folderNotFoundEdit: string
    folderNoAccess: string
}

export const defaultMessageKeys: DocumentFlatTreeMessageKeys = {
    noLink: 'file.pivot.document.error.no_link',
    noLinkEdit: 'file.pivot.document.error.no_link.edit',
    folderNotFound: 'file.pivot.document.error.folder_not_found',
    folderNotFoundEdit: 'file.pivot.document.error.folder_not_found.edit',
    folderNoAccess: 'file.pivot.document.error.folder_no_access',
}

export type DocumentFlatTreeProps = FlatTreeProps & {
    rootId: string
    messageKeys?: DocumentFlatTreeMessageKeys
    // Optional file/dossier reference (e.g. "TST0710") sent along with the prefetch
    // telemetry event so we can correlate request counts to specific dossiers.
    referenceNumber?: string
    sortOrder: DocumentSortOrder
    selectedOptions: string[]
    onOptionsSelect: (ids: string[], items: FolderDriveItem[]) => void
    // Folders are selectable via their hover action and included in Shift+click ranges
    // unless this is false (e.g. the task attachment picker, where only files make sense).
    allowFolderSelection?: boolean
    // Enables internal drag & drop. Called with the dragged items and the folder they
    // were dropped on; the host performs the Graph move and refreshes.
    onMoveItems?: (ids: string[], items: FolderDriveItem[], targetFolder: FolderDriveItem) => void
    setIsLoading?: (isLoading: boolean) => void
    onDriveItemsLoaded?: (allItems: FolderDriveItem[], filteredItems?: FolderDriveItem[]) => void
    setUpdateDriveItems?: (
        setter: (addDriveItems: DriveItem[], removeDriveItems: DriveItem[], filter?: string, sort?: 'asc' | 'desc') => void,
    ) => void
    setReloadDriveItems?: (setter: () => void) => void
    treeItemContent?: (
        flatTree: any,
        props: DocumentFlatTreeProps,
        itemProps: HeadlessFlatTreeItem<FolderDriveItem>,
        isContentLoading: boolean,
        fileCount: number | undefined,
    ) => React.ReactNode
}

export type DocumentFlatTreeItemProps = FlatTreeItemProps &
    DocumentFlatTreeProps & {
        item: FolderDriveItem
        flatTreeHook: HeadlessFlatTree<FolderDriveItem>
        isContentLoading?: boolean
        fileCount?: number
        // size: number | 'small' | 'medium'
    }

export type FolderDriveItem = HeadlessFlatTreeItemProps &
    DriveItem & {
        parent: FolderDriveItem
        displayName: string
        children: FolderDriveItem[]
        childCount?: number
    }

export const mapDriveItem = (item: DriveItem, parentItem: FolderDriveItem, rootId: string): FolderDriveItem => {
    return {
        ...item,
        value: item.id,
        parentValue: item.parentReference.id === rootId ? undefined : item.parentReference.id,
        itemType: item.folder ? 'branch' : 'leaf',
        parent: parentItem,
        displayName: item.folder ? formatFolderName(item.name) : item.name,
        children: [],
        childCount: item.folder?.childCount,
    }
}

const GRAPH_CHILDREN_PAGE_SIZE = 999
// Concurrency for the eager folder-only walk (DocumentCombobox via fetchFolderDriveItemTree).
// Kept low to avoid Graph throttling on dossiers with deep folder hierarchies (HAM-4146).
const GRAPH_FOLDER_TREE_LOAD_CONCURRENCY = 2
// Concurrency for the lazy expand-on-click loads in DocumentFlatTree.
const GRAPH_FOLDER_LAZY_LOAD_CONCURRENCY = 2
// How many layers of folder contents DocumentFlatTree eagerly prefetches on load. A value of
// 3 means we fetch the children of folders at the root-folder level (layer 1), at the next
// level (layer 2), and at the level below that (layer 3) — so items down to layer 4 are
// visible up front and folders at layer 3 already know their own recursive document counts.
// Only contents below layer 3 stay lazy.
const PREFETCH_DEPTH = 3

const PATIENT_RETRY_HEADERS = { SuppressErrors: 'true', [PATIENT_RETRY_HEADER]: 'true' }

const createConcurrencyGate = (limit: number) => {
    let inFlight = 0
    const waiters: Array<() => void> = []
    const next = () => {
        if (inFlight >= limit) {
            return
        }
        const waiter = waiters.shift()
        if (waiter) {
            inFlight++
            waiter()
        }
    }
    return <T,>(task: () => Promise<T>): Promise<T> =>
        new Promise<T>((resolve, reject) => {
            waiters.push(() => {
                task()
                    .then(resolve, reject)
                    .finally(() => {
                        inFlight--
                        next()
                    })
            })
            next()
        })
}

type ConcurrencyGate = <T>(task: () => Promise<T>) => Promise<T>

const fetchDriveItemChildren = (
    axios: Axios,
    id: string,
    nextLink?: string,
    filter?: string,
    gate?: ConcurrencyGate,
    onRequest?: () => void,
): Promise<IGraphArrayResponse<DriveItem>> => {
    const doRequest = (): Promise<IGraphArrayResponse<DriveItem>> => {
        onRequest?.()
        return nextLink
            ? (axios.get(nextLink, { baseURL: Destination.Documents, headers: PATIENT_RETRY_HEADERS }) as Promise<IGraphArrayResponse<DriveItem>>)
            : (axios.get(`/items/${id}/children`, {
                  baseURL: Destination.Documents,
                  params: { $select: driveItemOverviewProperties.join(','), $top: GRAPH_CHILDREN_PAGE_SIZE, $filter: filter },
                  headers: PATIENT_RETRY_HEADERS,
              }) as Promise<IGraphArrayResponse<DriveItem>>)
    }

    const request = gate ? gate(doRequest) : doRequest()

    return request.then((response: IGraphArrayResponse<DriveItem>) => {
        if (response.data['@odata.nextLink']) {
            return fetchDriveItemChildren(axios, id, response.data['@odata.nextLink'], filter, gate, onRequest).then(response2 => ({
                ...response,
                ...response2,
                data: { ...response.data, ...response2.data, value: [...response.data?.value, ...response2.data?.value] },
            }))
        } else {
            return response
        }
    })
}

const mapResponse = (
    axios: Axios,
    driveItem: DriveItem,
    parentItem: FolderDriveItem,
    rootId: string,
    foldersOnly: boolean,
    gate: ConcurrencyGate,
    onRequest?: () => void,
): Promise<FolderDriveItem[]> => {
    return Promise.resolve(mapDriveItem(driveItem, parentItem, rootId)).then(folderDriveItem => {
        if (folderDriveItem.childCount > 0) {
            return (
                fetchDriveItemChildren(axios, folderDriveItem.id, undefined, foldersOnly ? 'folder ne null' : undefined, gate, onRequest)
                    .then(response =>
                        Promise.all(
                            (response.data?.value || []).map(child =>
                                mapResponse(axios, child, folderDriveItem, rootId, foldersOnly, gate, onRequest),
                            ),
                        ),
                    )
                    // Return a flat list [self, ...all descendants]. Each node keeps children:[] from
                    // mapDriveItem and a correct parent pointer; nesting into direct-children is done
                    // once by nestDriveItems in fetchFolderDriveItemTree so the combobox renders each
                    // folder exactly once instead of re-listing every descendant under each ancestor.
                    .then(subtrees => [folderDriveItem, ...subtrees.flat()])
            )
        } else {
            return Promise.resolve([folderDriveItem])
        }
    })
}

export const fetchFolderDriveItemTree = (
    axios: Axios,
    translate: (key: string) => string,
    id: string,
    foldersOnly: boolean = false,
    concurrency: number = GRAPH_FOLDER_TREE_LOAD_CONCURRENCY,
    referenceNumber?: string,
    telemetryName: string = 'DocumentCombobox.FetchFolderTree',
): Promise<FolderDriveItem[]> => {
    const gate = createConcurrencyGate(concurrency)
    const stats = { requests: 0 }
    const startedAt = performance.now()
    return axios
        .get<DriveItem, AxiosResponse<DriveItem>>(`/items/${id}`, {
            baseURL: Destination.Documents,
            params: { $select: driveItemOverviewProperties.join(',') },
            headers: PATIENT_RETRY_HEADERS,
        })
        .then(response => {
            if (response.status === 200) {
                return mapResponse(axios, response.data, undefined, response.data.id, foldersOnly, gate, () => {
                    stats.requests += 1
                }).then(result => {
                    // Nest the flat list so each folder carries only its DIRECT children, then drop
                    // the dossier root itself — the combobox lists selectable subfolders, and the
                    // root's direct children remain the top level (their parent is the root, which
                    // is no longer in the list). This mirrors the pre-loaded (nestDriveItems) shape
                    // the documents screen already feeds to DocumentCombobox.
                    const folders = (nestDriveItems(result) ?? []).filter(item => item.id !== response.data.id)
                    const durationMs = Math.round(performance.now() - startedAt)
                    console.debug(
                        `[${telemetryName}] folder-tree fetched rootId=${id} foldersOnly=${foldersOnly} requests=${stats.requests} in ${durationMs}ms`,
                    )
                    appInsights.trackEvent({
                        name: telemetryName,
                        properties: {
                            referenceNumber,
                            rootId: id,
                            foldersOnly,
                            concurrency,
                            requests: stats.requests,
                            durationMs,
                            items: folders.length,
                            success: true,
                        },
                    })
                    return folders
                })
            }
            return Promise.reject(`${response.status}: ${response.statusText}`)
        })
        .catch(error => {
            const durationMs = Math.round(performance.now() - startedAt)
            const errorStatus = error?.response?.status ?? error?.status
            appInsights.trackEvent({
                name: telemetryName,
                properties: {
                    referenceNumber,
                    rootId: id,
                    foldersOnly,
                    concurrency,
                    requests: stats.requests,
                    durationMs,
                    success: false,
                    errorStatus,
                },
            })
            if (errorStatus === 404) {
                return Promise.reject(translate(defaultMessageKeys.folderNotFound) + ' ' + translate(defaultMessageKeys.folderNotFoundEdit))
            } else if (errorStatus === 403) {
                return Promise.reject(translate(defaultMessageKeys.folderNoAccess))
            }
            return Promise.reject(error)
        })
}

// Microsoft Graph JSON batching caps a single $batch at 20 sub-requests. We fetch the folder
// tree one boom-level at a time and bundle each level's sibling folders into batches of 20 so a
// wide dossier that used to take one round-trip per folder now takes ~one per 20 (HAM-4146).
const GRAPH_BATCH_MAX_REQUESTS = 20
// Concurrent $batch HTTP calls. Kept low (like the per-folder walk) to avoid Graph throttling
// (HAM-4146); each call already bundles up to 20 sub-requests. Raising it does not speed up a
// cold load — Graph/SharePoint paces the total throughput server-side (measured: ~70s at both 2
// and 5 for a 1717-folder dossier), so the win is fewer round-trips, not more parallelism.
const GRAPH_BATCH_CONCURRENCY = 2
const GRAPH_BATCH_MAX_THROTTLE_RETRIES = 10
const RETRYABLE_SUB_STATUSES = new Set([429, 503, 504])

type GraphBatchResponse = {
    responses?: Array<{
        id: string
        status: number
        headers?: Record<string, string>
        body?: { value?: DriveItem[]; '@odata.nextLink'?: string }
    }>
}

const chunk = <T,>(items: T[], size: number): T[][] => {
    const chunks: T[][] = []
    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size))
    }
    return chunks
}

const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

// Fetches the folder children of up to GRAPH_BATCH_MAX_REQUESTS folders in a single Graph $batch
// request, returning a map of parent-folder id -> its child DriveItems. Per-sub-response 429/503
// throttling is retried here with Retry-After: the graphRetry axios interceptor only sees the
// outer 200, not the throttled inner sub-responses. A 403/404 on a subfolder is treated as "no
// accessible subfolders" so the rest of the tree still loads.
const fetchFolderChildrenBatch = (
    axios: Axios,
    driveId: string,
    ids: string[],
    filter: string | undefined,
    onRequest: (() => void) | undefined,
    attempt: number = 0,
): Promise<Map<string, DriveItem[]>> => {
    if (ids.length === 0) {
        return Promise.resolve(new Map())
    }
    const query = [
        `$select=${encodeURIComponent(driveItemOverviewProperties.join(','))}`,
        `$top=${GRAPH_CHILDREN_PAGE_SIZE}`,
        ...(filter ? [`$filter=${encodeURIComponent(filter)}`] : []),
    ].join('&')
    const requests = ids.map(id => {
        onRequest?.()
        return { id, method: 'GET', url: `/drives/${driveId}/items/${id}/children?${query}` }
    })
    return axios
        .post<GraphBatchResponse>('/$batch', { requests }, { baseURL: Destination.SharePoint, headers: PATIENT_RETRY_HEADERS })
        .then(async response => {
            const result = new Map<string, DriveItem[]>()
            const throttled: string[] = []
            let retryAfterMs = 0
            for (const sub of response.data?.responses ?? []) {
                if (sub.status >= 200 && sub.status < 300) {
                    const value = sub.body?.value ?? []
                    // Follow pagination for the rare folder with more than GRAPH_CHILDREN_PAGE_SIZE subfolders.
                    if (sub.body?.['@odata.nextLink']) {
                        const rest = await fetchDriveItemChildren(axios, sub.id, sub.body['@odata.nextLink'], filter).then(
                            paged => paged.data?.value ?? [],
                        )
                        result.set(sub.id, [...value, ...rest])
                    } else {
                        result.set(sub.id, value)
                    }
                } else if (RETRYABLE_SUB_STATUSES.has(sub.status)) {
                    throttled.push(sub.id)
                    retryAfterMs = Math.max(retryAfterMs, parseRetryAfter(sub.headers?.['Retry-After'] ?? sub.headers?.['retry-after']) ?? 0)
                } else {
                    // 403/404/other: no accessible subfolders under this folder.
                    result.set(sub.id, [])
                }
            }
            if (throttled.length > 0 && attempt < GRAPH_BATCH_MAX_THROTTLE_RETRIES) {
                await sleep(retryAfterMs || Math.min(30_000, 500 * 2 ** attempt))
                const retried = await fetchFolderChildrenBatch(axios, driveId, throttled, filter, undefined, attempt + 1)
                retried.forEach((children, id) => result.set(id, children))
            } else {
                // Out of retries: treat still-throttled folders as empty so the rest of the tree resolves.
                throttled.forEach(id => result.set(id, result.get(id) ?? []))
            }
            return result
        })
}

// Breadth-first, batched variant of fetchFolderDriveItemTree used by DocumentCombobox: it produces
// the exact same nested FolderDriveItem[] (root dropped) but collapses the one-request-per-folder
// crawl into one $batch per ~20 sibling folders. Falls back to the recursive crawl when the drive
// id is unavailable (cannot build $batch sub-request URLs without it).
export const fetchFolderDriveItemTreeBatched = (
    axios: Axios,
    translate: (key: string) => string,
    driveId: string | undefined,
    id: string,
    foldersOnly: boolean = false,
    referenceNumber?: string,
    telemetryName: string = 'DocumentCombobox.FetchFolderTree',
): Promise<FolderDriveItem[]> => {
    if (!driveId) {
        return fetchFolderDriveItemTree(axios, translate, id, foldersOnly, undefined, referenceNumber, telemetryName)
    }
    const filter = foldersOnly ? 'folder ne null' : undefined
    const gate = createConcurrencyGate(GRAPH_BATCH_CONCURRENCY)
    const stats = { requests: 0 }
    const startedAt = performance.now()
    return axios
        .get<DriveItem, AxiosResponse<DriveItem>>(`/items/${id}`, {
            baseURL: Destination.Documents,
            params: { $select: driveItemOverviewProperties.join(',') },
            headers: PATIENT_RETRY_HEADERS,
        })
        .then(async response => {
            if (response.status !== 200) {
                return Promise.reject(`${response.status}: ${response.statusText}`)
            }
            const rootData = response.data
            const rootItem = mapDriveItem(rootData, undefined, rootData.id)
            const result: FolderDriveItem[] = [rootItem]
            let frontier: FolderDriveItem[] = (rootItem.childCount ?? 0) > 0 ? [rootItem] : []
            while (frontier.length > 0) {
                const parentById = new Map(frontier.map(parent => [parent.id, parent]))
                const maps = await Promise.all(
                    chunk(frontier, GRAPH_BATCH_MAX_REQUESTS).map(batch =>
                        gate(() =>
                            fetchFolderChildrenBatch(
                                axios,
                                driveId,
                                batch.map(parent => parent.id),
                                filter,
                                () => {
                                    stats.requests += 1
                                },
                            ),
                        ),
                    ),
                )
                const next: FolderDriveItem[] = []
                for (const map of maps) {
                    for (const [parentId, children] of map) {
                        const parent = parentById.get(parentId)
                        for (const childData of children) {
                            const childItem = mapDriveItem(childData, parent, rootData.id)
                            result.push(childItem)
                            if ((childItem.childCount ?? 0) > 0) {
                                next.push(childItem)
                            }
                        }
                    }
                }
                frontier = next
            }
            const folders = (nestDriveItems(result) ?? []).filter(item => item.id !== rootData.id)
            const durationMs = Math.round(performance.now() - startedAt)
            console.debug(
                `[${telemetryName}] folder-tree fetched (batched) rootId=${id} foldersOnly=${foldersOnly} requests=${stats.requests} in ${durationMs}ms`,
            )
            appInsights.trackEvent({
                name: telemetryName,
                properties: {
                    referenceNumber,
                    rootId: id,
                    foldersOnly,
                    batched: true,
                    requests: stats.requests,
                    durationMs,
                    items: folders.length,
                    success: true,
                },
            })
            return folders
        })
        .catch(error => {
            const durationMs = Math.round(performance.now() - startedAt)
            const errorStatus = error?.response?.status ?? error?.status
            appInsights.trackEvent({
                name: telemetryName,
                properties: {
                    referenceNumber,
                    rootId: id,
                    foldersOnly,
                    batched: true,
                    requests: stats.requests,
                    durationMs,
                    success: false,
                    errorStatus,
                },
            })
            if (errorStatus === 404) {
                return Promise.reject(translate(defaultMessageKeys.folderNotFound) + ' ' + translate(defaultMessageKeys.folderNotFoundEdit))
            } else if (errorStatus === 403) {
                return Promise.reject(translate(defaultMessageKeys.folderNoAccess))
            }
            return Promise.reject(error)
        })
}

// Returns a depth-first traversal so the result matches what FlatTree renders
// (FlatTree walks each parent's childrenValues in source order). Consumers like
// FileDocumentOverview rely on this to compute prev/next over the visible leafs.
const nestDriveItems = (items: FolderDriveItem[] | undefined): FolderDriveItem[] | undefined => {
    if (!items) {
        return items
    }
    const copies = new Map<string, FolderDriveItem>()
    const sourceOrder: FolderDriveItem[] = []
    for (const item of items) {
        const copy = { ...item, children: [] as FolderDriveItem[] }
        copies.set(item.id, copy)
        sourceOrder.push(copy)
    }
    const roots: FolderDriveItem[] = []
    for (const item of sourceOrder) {
        const parentCopy = item.parent?.id ? copies.get(item.parent.id) : undefined
        if (parentCopy) {
            parentCopy.children.push(item)
        } else {
            roots.push(item)
        }
    }
    const result: FolderDriveItem[] = []
    const visit = (node: FolderDriveItem) => {
        result.push(node)
        for (const child of node.children) {
            visit(child)
        }
    }
    roots.forEach(visit)
    return result
}

// Leaf-to-leaf comparison for the chosen sort order, without any tree-awareness. Shared by
// the tree comparator below and by DocumentFlatList, where all files sit in one flat list.
export const compareDriveItemsFlat =
    (sortOrder: DocumentSortOrder) =>
    (a: FolderDriveItem, b: FolderDriveItem): number => {
        switch (sortOrder) {
            case DocumentSortOrder.NameAsc:
                return a.name.localeCompare(b.name)
            case DocumentSortOrder.NameDesc:
                return b.name.localeCompare(a.name)
            case DocumentSortOrder.ModifiedAsc:
                return DateTime.fromISO(a.lastModifiedDateTime).toMillis() < DateTime.fromISO(b.lastModifiedDateTime).toMillis() ? -1 : 1
            case DocumentSortOrder.ModifiedDesc:
                return DateTime.fromISO(b.lastModifiedDateTime).toMillis() < DateTime.fromISO(a.lastModifiedDateTime).toMillis() ? -1 : 1
        }
        return 0
    }

const sortDriveItems = (
    driveItems: FolderDriveItem[],
    sortOrder: DocumentSortOrder,
    rootId: string,
): ((a: FolderDriveItem, b: FolderDriveItem) => number) => {
    // Items without a parent pointer (e.g. root-level files merged in via updateDriveItems or
    // search, where the dossier root itself is not part of the list) belong to the root.
    const parentIdOf = (item: FolderDriveItem) => item.parent?.id ?? rootId
    return (a: FolderDriveItem, b: FolderDriveItem) => {
        //Put first level items at the top.
        if (parentIdOf(a) === rootId && parentIdOf(b) !== rootId) {
            return -1
        } else if (parentIdOf(b) === rootId && parentIdOf(a) !== rootId) {
            return 1
        }

        const parentAIndex = driveItems.indexOf(driveItems.find(item => item.id === parentIdOf(a)))
        const parentBIndex = driveItems.indexOf(driveItems.find(item => item.id === parentIdOf(b)))
        if (parentAIndex < parentBIndex) {
            return parentAIndex
        } else if (parentAIndex > parentBIndex) {
            return parentBIndex
        }

        //Put branches at the top, leafs at the bottom.
        //Specifically used for where folders and documents are children of a folder.
        if (a.itemType === 'branch' && b.itemType !== 'branch') {
            return -1
        } else if (b.itemType === 'branch' && a.itemType !== 'branch') {
            return 1
        }

        //Folders are always sorted alphabetically, regardless of the chosen sort order for files.
        if (a.itemType === 'branch' && b.itemType === 'branch') {
            return a.name.localeCompare(b.name)
        }

        return compareDriveItemsFlat(sortOrder)(a, b)
    }
}

export default function DocumentFlatTree(props: DocumentFlatTreeProps): ReactElement {
    const axios = useAxios()
    const appStyles = useApplicationStyles()
    const styles = useStyles()
    useDragStaticStyles()
    const { t: translate } = useTranslation()
    const messageKeys: DocumentFlatTreeMessageKeys = { ...defaultMessageKeys, ...props.messageKeys }

    const [errorMessage, setErrorMessage] = useState<{ message: string; intent: 'warning' | 'error' }>(undefined)
    const [rootFolder, setRootFolder] = useState<FolderDriveItem>(undefined)
    const [allDriveItems, setAllDriveItems] = useState<FolderDriveItem[]>(undefined)
    // Synchronous mirror of allDriveItems for lookups inside async flows (prefetch chains)
    // that can't wait for React's batched state updater to commit.
    const allDriveItemsRef = useRef<FolderDriveItem[]>(undefined)
    const [filteredDriveItems, setFilteredDriveItems] = useState<FolderDriveItem[]>(undefined)
    const [openItems, setOpenItems] = useState<string[]>([])
    // loadedFolders: folder ids whose direct children have been fetched. Includes the root id
    // after the initial root-folder list arrives. loadingFolders: folder ids whose children
    // fetch is currently in flight (drives the inline spinner on the row).
    // Refs mirror the state so concurrent prefetch tasks can race-check synchronously.
    const [loadedFolders, setLoadedFolders] = useState<Set<string>>(new Set())
    const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set())
    const loadedFoldersRef = useRef<Set<string>>(loadedFolders)
    const loadingFoldersRef = useRef<Set<string>>(loadingFolders)
    const loadEpochRef = useRef(0)
    // One persistent concurrency gate for lazy expand-on-click loads, so a burst of expands
    // is serialised across the component's lifetime rather than per reload.
    const lazyGateRef = useRef<ConcurrencyGate | null>(null)
    if (!lazyGateRef.current) {
        lazyGateRef.current = createConcurrencyGate(GRAPH_FOLDER_LAZY_LOAD_CONCURRENCY)
    }

    const markLoading = useCallback((folderId: string) => {
        const next = new Set(loadingFoldersRef.current)
        next.add(folderId)
        loadingFoldersRef.current = next
        setLoadingFolders(next)
    }, [])
    const unmarkLoading = useCallback((folderId: string) => {
        if (!loadingFoldersRef.current.has(folderId)) {
            return
        }
        const next = new Set(loadingFoldersRef.current)
        next.delete(folderId)
        loadingFoldersRef.current = next
        setLoadingFolders(next)
    }, [])
    const markLoaded = useCallback((folderId: string) => {
        if (loadedFoldersRef.current.has(folderId)) {
            return
        }
        const next = new Set(loadedFoldersRef.current)
        next.add(folderId)
        loadedFoldersRef.current = next
        setLoadedFolders(next)
    }, [])
    const resetLoadState = useCallback((initialLoaded: string[] = []) => {
        loadedFoldersRef.current = new Set(initialLoaded)
        loadingFoldersRef.current = new Set()
        setLoadedFolders(loadedFoldersRef.current)
        setLoadingFolders(loadingFoldersRef.current)
    }, [])
    // Helper that keeps the ref and the state in lockstep so async flows can read the latest
    // items synchronously.
    const replaceAllDriveItems = useCallback((next: FolderDriveItem[] | undefined) => {
        allDriveItemsRef.current = next
        setAllDriveItems(next)
    }, [])
    // An empty folder (childCount = 0) has nothing more to fetch and can be treated as
    // fully loaded immediately — that keeps it out of the prefetch queue and lets its
    // ancestors qualify as fully loaded so they can show a real document count.
    const markEmptyFoldersLoaded = useCallback((folders: FolderDriveItem[]) => {
        const emptyIds = folders.filter(item => item.folder && (item.childCount ?? 0) === 0).map(item => item.id)
        if (emptyIds.length === 0) {
            return
        }
        const next = new Set(loadedFoldersRef.current)
        for (const id of emptyIds) {
            next.add(id)
        }
        loadedFoldersRef.current = next
        setLoadedFolders(next)
    }, [])

    const driveItems: FolderDriveItem[] = useMemo(
        () => (filteredDriveItems ? filteredDriveItems : allDriveItems),
        [filteredDriveItems, allDriveItems],
    )

    // A folder's recursive descendant set is "fully known" only when its own children have
    // been loaded AND every descendant folder is itself fully loaded. Used to decide whether
    // we can show a real document count or must show "?".
    const fullyLoadedFolders = useMemo(() => {
        const result = new Set<string>()
        if (!allDriveItems) {
            return result
        }
        const childrenByParent = new Map<string, FolderDriveItem[]>()
        for (const item of allDriveItems) {
            const parentId = item.parent?.id
            if (!parentId) {
                continue
            }
            const bucket = childrenByParent.get(parentId)
            if (bucket) {
                bucket.push(item)
            } else {
                childrenByParent.set(parentId, [item])
            }
        }
        const visiting = new Set<string>()
        const check = (folderId: string): boolean => {
            if (result.has(folderId)) {
                return true
            }
            if (!loadedFolders.has(folderId)) {
                return false
            }
            if (visiting.has(folderId)) {
                return false
            }
            visiting.add(folderId)
            const children = childrenByParent.get(folderId) ?? []
            for (const child of children) {
                if (child.folder && !check(child.id)) {
                    visiting.delete(folderId)
                    return false
                }
            }
            visiting.delete(folderId)
            result.add(folderId)
            return true
        }
        for (const item of allDriveItems) {
            if (item.folder) {
                check(item.id)
            }
        }
        return result
    }, [allDriveItems, loadedFolders])

    const fileCountByFolderId = useMemo(() => {
        const result = new Map<string, number>()
        if (!allDriveItems) {
            return result
        }
        const childrenByParent = new Map<string, FolderDriveItem[]>()
        for (const item of allDriveItems) {
            const parentId = item.parent?.id
            if (!parentId) {
                continue
            }
            const bucket = childrenByParent.get(parentId)
            if (bucket) {
                bucket.push(item)
            } else {
                childrenByParent.set(parentId, [item])
            }
        }
        const countFiles = (folderId: string): number => {
            const cached = result.get(folderId)
            if (cached !== undefined) {
                return cached
            }
            let count = 0
            const children = childrenByParent.get(folderId)
            if (children) {
                for (const child of children) {
                    count += child.folder ? countFiles(child.id) : 1
                }
            }
            result.set(folderId, count)
            return count
        }
        for (const item of allDriveItems) {
            if (item.folder) {
                countFiles(item.id)
            }
        }
        return result
    }, [allDriveItems])

    const updateDriveItemsFunc = useCallback(
        (addDriveItems?: DriveItem[], removeDriveItems?: DriveItem[]) => {
            const prevItems = allDriveItemsRef.current ?? []
            const removeDriveItemIds = removeDriveItems?.map(driveItem => driveItem.id) || []
            const existingDriveItems = prevItems.filter(prevItem => !removeDriveItemIds.includes(prevItem.id))
            const result = [
                ...existingDriveItems,
                ...(addDriveItems?.map(driveItem =>
                    mapDriveItem(
                        driveItem,
                        prevItems.find(folderDriveItem => driveItem.parentReference.id === folderDriveItem.id),
                        props.rootId,
                    ),
                ) || []),
            ]
            result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
            replaceAllDriveItems(result)
        },
        [props.sortOrder, props.rootId, replaceAllDriveItems],
    )

    // Fetches a folder's direct children (folders + docs), merges into allDriveItems, and
    // updates load-tracking. Promise resolves with the newly mapped children so callers can
    // chain prefetch recursion. Returns [] when the folder is already loaded/loading or when
    // the call fails (patient-retry exhausted) — the resolved value never throws.
    // `stats` is an optional tally that the prefetch path uses to count every Graph HTTP
    // request (including pagination follow-ups) so we can monitor throttling risk.
    const fetchAndMergeChildren = useCallback(
        (folderId: string, epoch: number, stats?: { requests: number }): Promise<FolderDriveItem[]> => {
            if (loadedFoldersRef.current.has(folderId) || loadingFoldersRef.current.has(folderId)) {
                return Promise.resolve([])
            }

            markLoading(folderId)

            return fetchDriveItemChildren(
                axios,
                folderId,
                undefined,
                undefined,
                lazyGateRef.current,
                stats
                    ? () => {
                          stats.requests += 1
                      }
                    : undefined,
            )
                .then(response => {
                    if (epoch !== loadEpochRef.current) {
                        return [] as FolderDriveItem[]
                    }
                    const current = allDriveItemsRef.current
                    const parentItem = current?.find(item => item.id === folderId)
                    if (!current || !parentItem) {
                        return [] as FolderDriveItem[]
                    }
                    const mappedChildren = (response.data?.value || []).map(child => mapDriveItem(child, parentItem, props.rootId))
                    const merged = [...current, ...mappedChildren]
                    merged.sort(sortDriveItems(merged, props.sortOrder, props.rootId))
                    replaceAllDriveItems(merged)
                    markLoaded(folderId)
                    markEmptyFoldersLoaded(mappedChildren)
                    return mappedChildren
                })
                .catch(() => {
                    // Patient retry has already exhausted its budget. Leave the folder in the
                    // unloaded state so a re-open by the user can trigger another fetch.
                    return [] as FolderDriveItem[]
                })
                .finally(() => {
                    if (epoch === loadEpochRef.current) {
                        unmarkLoading(folderId)
                    }
                })
        },
        [axios, markEmptyFoldersLoaded, markLoaded, markLoading, props.rootId, props.sortOrder, replaceAllDriveItems, unmarkLoading],
    )

    // Recursively prefetch a subtree up to `depthRemaining` levels below `folder`. Empty
    // folders short-circuit. Walks via fetchAndMergeChildren so the same gate / load-state /
    // patient-retry path is used as user-triggered loads. The `stats` tally accumulates the
    // Graph request count across the whole prefetch tree.
    const prefetchSubtree = useCallback(
        (folder: FolderDriveItem, depthRemaining: number, epoch: number, stats: { requests: number }): Promise<void> => {
            if (depthRemaining <= 0) {
                return Promise.resolve()
            }
            if (epoch !== loadEpochRef.current) {
                return Promise.resolve()
            }
            if ((folder.childCount ?? 0) === 0) {
                return Promise.resolve()
            }
            return fetchAndMergeChildren(folder.id, epoch, stats).then(children => {
                if (depthRemaining <= 1 || epoch !== loadEpochRef.current) {
                    return undefined
                }
                const subfolders = children.filter(child => child.folder && (child.childCount ?? 0) > 0)
                return Promise.all(subfolders.map(sub => prefetchSubtree(sub, depthRemaining - 1, epoch, stats))).then(() => undefined)
            })
        },
        [fetchAndMergeChildren],
    )

    const reloadDriveItemsFunc = useCallback(() => {
        setErrorMessage(undefined)
        setFilteredDriveItems(undefined)
        resetLoadState()

        if (!props.rootId) {
            replaceAllDriveItems(undefined)
            return
        }

        loadEpochRef.current += 1
        const epoch = loadEpochRef.current

        props.setIsLoading?.(true)

        axios
            .get<DriveItem, AxiosResponse<DriveItem>>(`/items/${props.rootId}`, {
                baseURL: Destination.Documents,
                params: { $select: driveItemOverviewProperties.join(',') },
                headers: PATIENT_RETRY_HEADERS,
            })
            .then(response => {
                if (epoch !== loadEpochRef.current) {
                    return undefined
                }
                if (response.status !== 200) {
                    return Promise.reject(`${response.status}: ${response.statusText}`)
                }
                const root = mapDriveItem(response.data, undefined, response.data.id)
                setRootFolder(root)
                // No folders-only filter here: files that live directly in the dossier root
                // have no folder row to expand, so this initial load is their only chance to
                // appear (HAM-4054 regression).
                return fetchDriveItemChildren(axios, props.rootId, undefined, undefined, lazyGateRef.current).then(childrenResponse => {
                    if (epoch !== loadEpochRef.current) {
                        return undefined
                    }
                    const rootItems = (childrenResponse.data?.value || []).map(child => mapDriveItem(child, root, props.rootId))
                    rootItems.sort(sortDriveItems(rootItems, props.sortOrder, props.rootId))
                    replaceAllDriveItems(rootItems)
                    resetLoadState([props.rootId])
                    markEmptyFoldersLoaded(rootItems)
                    props.setIsLoading?.(false)
                    // Eagerly prefetch the first PREFETCH_DEPTH layers below each root folder
                    // so the user sees real document counts and depth-3 content without having
                    // to click. Deeper folders stay lazy.
                    const rootFolders = rootItems.filter(item => item.folder)
                    if (rootFolders.length > 0) {
                        const prefetchStart = performance.now()
                        const stats = { requests: 0 }
                        Promise.all(rootFolders.map(rf => prefetchSubtree(rf, PREFETCH_DEPTH, epoch, stats))).then(() => {
                            if (epoch !== loadEpochRef.current) {
                                return
                            }
                            const durationMs = Math.round(performance.now() - prefetchStart)
                            console.debug(`[DocumentFlatTree] prefetch done depth=${PREFETCH_DEPTH} requests=${stats.requests} in ${durationMs}ms`)
                            appInsights.trackEvent({
                                name: 'DocumentFlatTree.Prefetch',
                                properties: {
                                    referenceNumber: props.referenceNumber,
                                    rootId: props.rootId,
                                    depth: PREFETCH_DEPTH,
                                    requests: stats.requests,
                                    durationMs,
                                    rootFolders: rootFolders.length,
                                },
                            })
                        })
                    }
                })
            })
            .catch(error => {
                if (epoch !== loadEpochRef.current) {
                    return
                }
                replaceAllDriveItems([])
                props.setIsLoading?.(false)
                const errorStatus = error?.response?.status ?? error?.status
                if (errorStatus === 404) {
                    setErrorMessage({
                        message: translate(messageKeys.folderNotFound) + ' ' + translate(messageKeys.folderNotFoundEdit),
                        intent: 'warning',
                    })
                } else if (errorStatus === 403) {
                    setErrorMessage({ message: translate(messageKeys.folderNoAccess), intent: 'error' })
                } else {
                    setErrorMessage({ message: error?.toString?.() ?? String(error), intent: 'error' })
                }
            })
    }, [
        axios,
        markEmptyFoldersLoaded,
        messageKeys,
        prefetchSubtree,
        props.referenceNumber,
        props.rootId,
        props.setIsLoading,
        props.sortOrder,
        replaceAllDriveItems,
        resetLoadState,
        translate,
    ])

    const loadFolderChildren = useCallback(
        (folderId: string) => {
            fetchAndMergeChildren(folderId, loadEpochRef.current)
        },
        [fetchAndMergeChildren],
    )

    useEffect(() => {
        setFilteredDriveItems(prevItems => {
            if (prevItems) {
                const result = [...prevItems]
                result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
                return result
            }
            return prevItems
        })
        const prevAll = allDriveItemsRef.current
        if (prevAll) {
            const result = [...prevAll]
            result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
            replaceAllDriveItems(result)
        }
    }, [props.sortOrder, props.rootId, replaceAllDriveItems])

    useEffect(() => {
        if (loadingFolders.size > 0) {
            return
        }
        props.onDriveItemsLoaded?.(nestDriveItems(allDriveItems), nestDriveItems(filteredDriveItems))
    }, [allDriveItems, filteredDriveItems, loadingFolders])

    useEffect(() => {
        props.setUpdateDriveItems?.((addDriveItems: DriveItem[], removeDriveItems: DriveItem[]) =>
            updateDriveItemsFunc(addDriveItems, removeDriveItems),
        )
        props.setReloadDriveItems?.(() => reloadDriveItemsFunc())

        return () => props.setUpdateDriveItems?.(() => {}) // Cleanup
    }, [])

    // Selection is owned by the delegated row-click handlers below (HAM-3993); the headless
    // tree only manages open/close, so no selectionMode (and thus no checkboxes).
    const flatTree = useHeadlessFlatTree_unstable(driveItems, {
        openItems: openItems,
        onOpenChange: (_, data) => {
            const folderId = data.value.toString()
            if (data.open) {
                setOpenItems(oldItems => [...new Set([...oldItems, folderId])])
                loadFolderChildren(folderId)
            } else {
                setOpenItems(oldItems => oldItems.filter(item => item !== folderId))
            }
        },
    })

    useEffect(() => {
        reloadDriveItemsFunc()
    }, [props.rootId])

    // ===== Selection (HAM-3993) =====
    // Row clicks own the selection: plain click toggles, Shift+click ranges, double-click
    // opens. The pending-click timer lets a double-click open a file without first toggling
    // its selection; a click on a *different* row flushes the pending toggle immediately so
    // rapid multi-select clicks never get lost.
    const treeContainerRef = useRef<HTMLDivElement>(null)
    const anchorIdRef = useRef<string | null>(null)
    const pendingClickRef = useRef<{ nodeId: string; timer: number } | null>(null)
    const selectedIdsRef = useRef<string[]>(props.selectedOptions ?? [])
    useEffect(() => {
        selectedIdsRef.current = props.selectedOptions ?? []
    }, [props.selectedOptions])
    useEffect(
        () => () => {
            if (pendingClickRef.current) {
                window.clearTimeout(pendingClickRef.current.timer)
            }
        },
        [],
    )

    // Drag state lives in refs, NOT React state: a re-render during dragover would replace
    // the row elements and break the native drag session.
    const dragStateRef = useRef({
        draggingIds: new Set<string>(),
        currentDropTarget: null as HTMLElement | null,
        dragInProgress: false,
        suppressNextClick: false,
    })
    // The root folder as drop target for the empty container space, kept in a ref so the
    // drag listeners never need to re-attach mid-drag.
    const rootFolderRef = useRef<FolderDriveItem>(undefined)
    rootFolderRef.current = rootFolder

    const treeRows = useMemo<TreeRow[]>(
        () => (driveItems ?? []).map(item => ({ id: item.id, parentId: item.parent?.id, isFolder: item.itemType === 'branch' })),
        [driveItems],
    )
    const parentByIdRef = useRef<Map<string, string | undefined>>(new Map())
    parentByIdRef.current = useMemo(() => new Map((driveItems ?? []).map(item => [item.id, item.parent?.id])), [driveItems])
    const filterActiveRef = useRef(false)
    filterActiveRef.current = filteredDriveItems !== undefined

    const commitSelection = useCallback(
        (ids: Iterable<string>) => {
            const idArray = [...ids]
            selectedIdsRef.current = idArray
            const items = idArray.map(id => allDriveItemsRef.current?.find(item => item.id === id)).filter(Boolean)
            props.onOptionsSelect?.(idArray, items)
        },
        [props.onOptionsSelect],
    )

    const applyToggle = useCallback(
        (nodeId: string) => {
            const result = toggleSelect(new Set(selectedIdsRef.current), nodeId)
            anchorIdRef.current = result.anchor
            commitSelection(result.next)
        },
        [commitSelection],
    )

    const flushPendingClick = useCallback(
        (apply: boolean) => {
            const pending = pendingClickRef.current
            if (!pending) {
                return
            }
            window.clearTimeout(pending.timer)
            pendingClickRef.current = null
            if (apply) {
                applyToggle(pending.nodeId)
            }
        },
        [applyToggle],
    )

    const getVisibleOrder = useCallback(() => Array.from(flatTree.items(), item => item.value as string), [flatTree])

    const handleExplorerClick = (event: React.MouseEvent<HTMLElement>) => {
        const drag = dragStateRef.current
        if (drag.suppressNextClick) {
            drag.suppressNextClick = false
            return
        }
        const target = event.target as HTMLElement
        // The folder/file select-toggle hover action (rendered by the row) is handled here via
        // event delegation, so the row component stays free of selection logic.
        const selectToggle = target.closest<HTMLElement>('[data-action="select-toggle"]')
        if (selectToggle?.dataset.nodeId) {
            flushPendingClick(true)
            applyToggle(selectToggle.dataset.nodeId)
            return
        }
        // Other buttons/links/menus in the row handle their own clicks.
        if (target.closest('button, a, [role="menuitem"], input')) {
            return
        }
        const row = target.closest<HTMLElement>('[data-node-id]')
        if (!row?.dataset.nodeId) {
            return
        }
        const nodeId = row.dataset.nodeId
        if (event.shiftKey) {
            flushPendingClick(true)
            const result = selectRange(
                treeRows,
                getVisibleOrder(),
                anchorIdRef.current,
                nodeId,
                new Set(selectedIdsRef.current),
                props.allowFolderSelection !== false,
            )
            anchorIdRef.current = result.anchor ?? nodeId
            commitSelection(result.next)
            return
        }
        if (row.dataset.nodeType === 'folder') {
            // Plain folder click expands/collapses (handled by FlatTree itself); selection of a
            // folder goes via its select-toggle hover action.
            return
        }
        if (event.detail > 1) {
            // Second click of a double-click: the dblclick handler opens the file.
            return
        }
        flushPendingClick(true)
        pendingClickRef.current = {
            nodeId,
            timer: window.setTimeout(() => {
                pendingClickRef.current = null
                applyToggle(nodeId)
            }, 200),
        }
    }

    // Shift+click on a folder row must range-select instead of expanding/collapsing, so it is
    // intercepted in the capture phase before FlatTree's own click handler runs.
    const handleExplorerClickCapture = (event: React.MouseEvent<HTMLElement>) => {
        if (!event.shiftKey || props.allowFolderSelection === false) {
            return
        }
        const target = event.target as HTMLElement
        if (target.closest('button, a, [role="menuitem"], input')) {
            return
        }
        const row = target.closest<HTMLElement>('[data-node-id][data-node-type="folder"]')
        if (!row?.dataset.nodeId) {
            return
        }
        event.stopPropagation()
        flushPendingClick(true)
        const result = selectRange(treeRows, getVisibleOrder(), anchorIdRef.current, row.dataset.nodeId, new Set(selectedIdsRef.current), true)
        anchorIdRef.current = result.anchor ?? row.dataset.nodeId
        commitSelection(result.next)
    }

    const handleExplorerDoubleClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = event.target as HTMLElement
        if (target.closest('button, a, [role="menuitem"], input')) {
            return
        }
        const row = target.closest<HTMLElement>('[data-node-id][data-node-type="file"]')
        if (!row?.dataset.nodeId) {
            return
        }
        // The two clicks that formed this double-click must not toggle the selection.
        flushPendingClick(false)
        stealthClick({ id: row.dataset.nodeId, destination: Destination.Documents, documentsOpen: DocumentOpenType.Open }, axios)
    }

    // Escape and Ctrl+A work document-wide (focus is usually NOT inside the tree after
    // clicking elsewhere on the page), but never while the user is typing in an input or
    // interacting with a dialog/menu — there the browser/component behavior must win.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            if (target?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"], [role="listbox"]')) {
                return
            }
            if (event.key === 'Escape') {
                flushPendingClick(false)
                if (selectedIdsRef.current.length > 0) {
                    anchorIdRef.current = null
                    commitSelection([])
                }
            } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
                event.preventDefault()
                flushPendingClick(false)
                const result = selectAllVisibleFiles(treeRows, getVisibleOrder())
                commitSelection(result.next)
            }
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [treeRows, getVisibleOrder, commitSelection, flushPendingClick])

    // ===== Drag & drop (HAM-3993): move items into a folder =====
    // Native listeners via event delegation on the wrapper (one set for the whole tree, so no
    // per-row listeners); drop feedback via classList on existing DOM (no re-renders mid-drag).
    // Reordering within a folder is intentionally not supported (parked in the ticket).
    useEffect(() => {
        const container = treeContainerRef.current
        if (!props.onMoveItems || !container) {
            return undefined
        }
        const drag = dragStateRef.current

        const setDropTarget = (row: HTMLElement | null) => {
            if (drag.currentDropTarget === row) {
                return
            }
            drag.currentDropTarget?.classList.remove('hammockDocTreeDropInside')
            drag.currentDropTarget = row
            row?.classList.add('hammockDocTreeDropInside')
        }

        const setRootDropTarget = (active: boolean) => {
            container.classList.toggle('hammockDocTreeDropRoot', active)
        }

        const clearDragMarkers = () => {
            setDropTarget(null)
            setRootDropTarget(false)
            container.classList.remove('hammockDocTreeDragActive')
            container.querySelectorAll('.hammockDocTreeDragging').forEach(el => el.classList.remove('hammockDocTreeDragging'))
        }

        const onDragStart = (event: DragEvent) => {
            const row = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-node-id]')
            if (!row?.dataset.nodeId) {
                return
            }
            // The filtered search view flattens the hierarchy; moving from that flat list is
            // ambiguous, so dragging is disabled while a filter is active.
            if (filterActiveRef.current) {
                event.preventDefault()
                return
            }
            flushPendingClick(true)
            const ids = getDragIds(new Set(selectedIdsRef.current), row.dataset.nodeId)
            drag.draggingIds = new Set(ids)
            drag.dragInProgress = true
            event.dataTransfer.effectAllowed = 'move'
            try {
                event.dataTransfer.setData(HAMMOCK_DRAG_MIME, JSON.stringify(ids))
                event.dataTransfer.setData('text/plain', ids.join(','))
            } catch {
                // Some hosts refuse custom mime types; drag.dragInProgress still marks it internal.
            }
            if (ids.length > 1) {
                const ghost = document.createElement('div')
                ghost.className = 'hammockDocTreeDragGhost'
                ghost.textContent = translate('file.pivot.document.drag.ghost', { count: ids.length })
                document.body.appendChild(ghost)
                event.dataTransfer.setDragImage(ghost, 0, 0)
                window.setTimeout(() => ghost.remove(), 0)
            }
            for (const id of ids) {
                container.querySelectorAll(`[data-node-id="${CSS.escape(id)}"]`).forEach(el => el.classList.add('hammockDocTreeDragging'))
            }
            if (canDropOnRoot(drag.draggingIds, props.rootId, parentByIdRef.current)) {
                container.classList.add('hammockDocTreeDragActive')
            }
        }

        const onDragOver = (event: DragEvent) => {
            const isInternal = drag.dragInProgress
            const isOsFileDrag = !isInternal && (event.dataTransfer?.types?.includes('Files') ?? false)
            if (!isInternal && !isOsFileDrag) {
                return
            }
            const row = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-node-id][data-node-type="folder"]')
            if (!row?.dataset.nodeId) {
                setDropTarget(null)
                // The empty container space (not a file row) targets the dossier root, for
                // internal drags of items that are not already at the top level. OS file drags
                // keep falling through to the Dropzone.
                const onAnyRow = !!(event.target as HTMLElement | null)?.closest('[data-node-id]')
                const rootDrop = isInternal && !onAnyRow && canDropOnRoot(drag.draggingIds, props.rootId, parentByIdRef.current)
                setRootDropTarget(rootDrop)
                if (rootDrop) {
                    event.preventDefault()
                    event.dataTransfer.dropEffect = 'move'
                }
                return
            }
            setRootDropTarget(false)
            if (isInternal) {
                if (drag.draggingIds.has(row.dataset.nodeId) || isRecursiveDrop(drag.draggingIds, row.dataset.nodeId, parentByIdRef.current)) {
                    setDropTarget(null)
                    return
                }
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
            }
            // For OS file drags the highlight marks the folder the import dialog will preselect;
            // preventDefault/drop handling stays with the surrounding Dropzone.
            setDropTarget(row)
        }

        const onDragLeave = (event: DragEvent) => {
            if (!container.contains(event.relatedTarget as Node | null)) {
                setDropTarget(null)
                setRootDropTarget(false)
            }
        }

        const onDrop = (event: DragEvent) => {
            const row = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-node-id][data-node-type="folder"]')
            const onAnyRow = !!(event.target as HTMLElement | null)?.closest('[data-node-id]')
            clearDragMarkers()
            if (!drag.dragInProgress) {
                // OS file drop: let it bubble to the Dropzone, which owns the upload flow.
                return
            }
            event.preventDefault()
            event.stopPropagation()
            const ids = [...drag.draggingIds]
            drag.draggingIds = new Set()
            drag.dragInProgress = false
            if (ids.length === 0) {
                return
            }
            let targetItem: FolderDriveItem
            if (row?.dataset.nodeId) {
                const targetId = row.dataset.nodeId
                if (isRecursiveDrop(new Set(ids), targetId, parentByIdRef.current)) {
                    return
                }
                targetItem = allDriveItemsRef.current?.find(item => item.id === targetId)
            } else if (!onAnyRow && canDropOnRoot(new Set(ids), props.rootId, parentByIdRef.current)) {
                // Drop on the empty container space: move to the dossier root (top level).
                targetItem = rootFolderRef.current
            }
            if (!targetItem) {
                return
            }
            // Items already inside the target folder would only produce a name conflict with
            // themselves — leave them where they are. An absent parent link means the item
            // already lives at the root.
            const items = ids
                .map(id => allDriveItemsRef.current?.find(item => item.id === id))
                .filter(Boolean)
                .filter(item => (item.parent?.id ?? props.rootId) !== targetItem.id)
            if (items.length === 0) {
                return
            }
            drag.suppressNextClick = true
            window.setTimeout(() => {
                drag.suppressNextClick = false
            }, 100)
            props.onMoveItems(
                items.map(item => item.id),
                items,
                targetItem,
            )
        }

        const onDragEnd = () => {
            clearDragMarkers()
            drag.draggingIds = new Set()
            drag.dragInProgress = false
        }

        container.addEventListener('dragstart', onDragStart)
        container.addEventListener('dragover', onDragOver)
        container.addEventListener('dragleave', onDragLeave)
        container.addEventListener('drop', onDrop)
        container.addEventListener('dragend', onDragEnd)
        return () => {
            container.removeEventListener('dragstart', onDragStart)
            container.removeEventListener('dragover', onDragOver)
            container.removeEventListener('dragleave', onDragLeave)
            container.removeEventListener('drop', onDrop)
            container.removeEventListener('dragend', onDragEnd)
        }
    }, [props.onMoveItems, props.rootId, flushPendingClick, translate])

    // Everything DocumentFlatTree adds on top of FlatTreeProps must stay off the DOM element,
    // so strip the custom props before spreading the remainder onto <FlatTree>.
    const {
        rootId: _rootId,
        messageKeys: _messageKeysProp,
        referenceNumber: _referenceNumber,
        sortOrder: _sortOrder,
        selectedOptions: _selectedOptions,
        onOptionsSelect: _onOptionsSelect,
        allowFolderSelection: _allowFolderSelection,
        onMoveItems: _onMoveItems,
        setIsLoading: _setIsLoading,
        onDriveItemsLoaded: _onDriveItemsLoaded,
        setUpdateDriveItems: _setUpdateDriveItems,
        setReloadDriveItems: _setReloadDriveItems,
        treeItemContent: _treeItemContent,
        ...flatTreeOnlyProps
    } = props

    if (!props.rootId) {
        return (
            <MessageBar intent={'warning'}>
                <MessageBarBody>
                    {translate(messageKeys.noLink)} {translate(messageKeys.noLinkEdit)}
                </MessageBarBody>
            </MessageBar>
        )
    }

    if (errorMessage) {
        return <MessageBar intent={errorMessage.intent}>{errorMessage.message}</MessageBar>
    }

    if (!allDriveItems) {
        return <Spinner label={translate('base.loading')} size={SpinnerSize.xSmall} labelPosition="right" />
    }

    return (
        <div className={mergeClasses(appStyles.flexColumn)}>
            <FileDocumentSearchBox
                {...props}
                placeholderKey={'file.pivot.document.search.placeholder'}
                componentProps={{ appearance: 'underline' }}
                rootFolder={rootFolder}
                driveItems={allDriveItems}
                setFilteredDriveItems={searchResults => {
                    const sortedResults = searchResults.sort(sortDriveItems(searchResults, props.sortOrder, props.rootId))
                    setFilteredDriveItems(sortedResults)
                    props.onOptionsSelect?.([], [])
                    setOpenItems(sortedResults.filter(item => item.itemType === 'branch').map(item => item.id))
                }}
                reset={() => {
                    setFilteredDriveItems(undefined)
                    props.onOptionsSelect?.([], [])
                    setOpenItems([])
                    reloadDriveItemsFunc()
                }}
            />
            {allDriveItems.length === 0 && (
                <MessageBar intent={'info'}>
                    <MessageBarBody>
                        {translate('file.pivot.document.empty', { defaultValue: 'Dit dossier bevat nog geen mappen of documenten.' })}
                    </MessageBarBody>
                </MessageBar>
            )}
            <div
                ref={treeContainerRef}
                onClick={handleExplorerClick}
                onClickCapture={handleExplorerClickCapture}
                onDoubleClick={handleExplorerDoubleClick}
                className={styles.dragAndDropSpacing}
            >
                <FlatTree
                    {...flatTreeOnlyProps}
                    {...flatTree.getTreeProps()}
                    aria-label={'FileDocumentTree'}
                    className={mergeClasses(styles.virtualizedTree, styles.explorerTree, props.className)}
                >
                    {Array.from(flatTree.items(), flatTreeItem => {
                        const treeItemProps = flatTreeItem.getTreeItemProps()
                        const folderId = treeItemProps.value as string
                        const isContentLoading = loadingFolders.has(folderId)
                        // Show a number only when the recursive document count is known with
                        // certainty — that requires this folder and every descendant folder to be
                        // loaded. Otherwise pass undefined so the row can render "?".
                        const fileCount: number | undefined = fullyLoadedFolders.has(folderId) ? (fileCountByFolderId.get(folderId) ?? 0) : undefined
                        return (
                            props.treeItemContent?.(flatTree, props, flatTreeItem, isContentLoading, fileCount) || (
                                <SimpleDocumentFlatTreeItem
                                    key={flatTreeItem.value}
                                    {...treeItemProps}
                                    {...props}
                                    item={treeItemProps}
                                    flatTreeHook={flatTree}
                                    itemType={flatTreeItem.itemType}
                                    size={props.size}
                                    isContentLoading={isContentLoading}
                                    fileCount={fileCount}
                                />
                            )
                        )
                    })}
                </FlatTree>
            </div>
        </div>
    )
}
